import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { Box, Typography } from "@mui/material";
import StandardLoadingIndicator from "components/StandardLoadingIndicator";
import { parseISO } from "date-fns";
import { IoTimeOutline } from "react-icons/io5";
import GlassCard from "components/GlassCard";
import { GRADIENTS } from "utils/designTokens";

// ---------------------------------------------------------------------------
// Status color mapping
// ---------------------------------------------------------------------------
const STATUS_COLORS = {
  RECRUITING: "#10B981",
  ACTIVE: "#3B82F6",
  NOT_YET_RECRUITING: "#60A5FA",
  COMPLETED: "#6B7280",
};

const getStatusColor = (status) => STATUS_COLORS[status] || "#6B7280";

// ---------------------------------------------------------------------------
// Date parser (handles "2025-12" partial dates)
// ---------------------------------------------------------------------------
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    return parseISO(dateStr + "-01");
  }
  return parseISO(dateStr);
};

// ---------------------------------------------------------------------------
// Truncate trial title for Y-axis labels
// ---------------------------------------------------------------------------
const truncateLabel = (text, maxLen = 38) => {
  if (!text) return "";
  return text.length > maxLen ? text.slice(0, maxLen).trim() + "..." : text;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const MAX_TIMELINE_TRIALS = 5;

const TimelineRadar = ({ trials, loading = false }) => {
  const { series, categories, chartOptions } = useMemo(() => {
    if (!trials || trials.length === 0) {
      return { series: [], categories: [], chartOptions: {} };
    }

    // Build data for each trial, take top 5 by enrollment
    const trialData = trials
      .map((trial) => {
        const start = parseDate(trial.start_date);
        const end = parseDate(trial.completion_date);
        if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return null;
        return {
          label: trial.nct_id,
          fullTitle: trial.official_title || "",
          nctId: trial.nct_id,
          sponsor: trial.sponsor || "",
          start: start.getTime(),
          end: end.getTime(),
          status: trial.status,
          enrollment: trial.enrollment || 0,
          color: getStatusColor(trial.status),
        };
      })
      .filter(Boolean)
      // Sort by enrollment descending to pick the top trials
      .sort((a, b) => b.enrollment - a.enrollment)
      .slice(0, MAX_TIMELINE_TRIALS)
      // Then re-sort by start date for the chart display
      .sort((a, b) => a.start - b.start);

    const cats = trialData.map((d) => d.label);

    const seriesData = [
      {
        data: trialData.map((d) => ({
          x: d.label,
          y: [d.start, d.end],
          fillColor: d.color,
          nctId: d.nctId,
          fullTitle: d.fullTitle,
          sponsor: d.sponsor,
          status: d.status,
          enrollment: d.enrollment,
        })),
      },
    ];

    const now = Date.now();

    const options = {
      chart: {
        type: "rangeBar",
        background: "transparent",
        toolbar: { show: false },
        fontFamily: "inherit",
        zoom: { enabled: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 6,
          barHeight: "55%",
          distributed: true,
        },
      },
      xaxis: {
        type: "datetime",
        labels: {
          style: { colors: "rgba(255,255,255,0.5)", fontSize: "11px" },
          datetimeFormatter: {
            year: "yyyy",
            month: "MMM yyyy",
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { colors: "rgba(255,255,255,0.7)", fontSize: "11px" },
          maxWidth: 280,
        },
      },
      grid: {
        borderColor: "rgba(255,255,255,0.06)",
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
      },
      annotations: {
        xaxis: [
          {
            x: now,
            borderColor: "#EF4444",
            strokeDashArray: 4,
            label: {
              text: "Today",
              borderColor: "#EF4444",
              style: {
                color: "#fff",
                background: "#EF4444",
                fontSize: "11px",
                fontWeight: 600,
                padding: { left: 8, right: 8, top: 4, bottom: 4 },
              },
              orientation: "horizontal",
              offsetY: -8,
            },
          },
        ],
      },
      tooltip: {
        custom: ({ dataPointIndex, w }) => {
          const point = w.config.series[0].data[dataPointIndex];
          if (!point) return "";
          const startDate = new Date(point.y[0]).toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          });
          const endDate = new Date(point.y[1]).toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          });
          return `
            <div style="padding:10px 14px;background:#1A1E3C;border:1px solid rgba(255,255,255,0.1);border-radius:8px;font-size:12px;color:#fff;max-width:340px;word-break:break-word;white-space:normal;">
              <div style="font-weight:700;margin-bottom:6px;">${point.nctId}</div>
              <div style="color:rgba(255,255,255,0.7);margin-bottom:4px;line-height:1.5;">${point.fullTitle}</div>
              ${point.sponsor ? `<div style="color:rgba(255,255,255,0.5);margin-bottom:4px;">Sponsor: ${point.sponsor}</div>` : ""}
              <div style="display:flex;gap:12px;margin-top:6px;flex-wrap:wrap;">
                <span style="color:${getStatusColor(point.status)};font-weight:600;">${point.status.replace(/_/g, " ")}</span>
                <span>n=${point.enrollment}</span>
              </div>
              <div style="color:rgba(255,255,255,0.5);margin-top:4px;">${startDate} \u2192 ${endDate}</div>
            </div>
          `;
        },
      },
      legend: { show: false },
      dataLabels: { enabled: false },
      states: {
        hover: { filter: { type: "lighten", value: 0.15 } },
      },
    };

    return { series: seriesData, categories: cats, chartOptions: options };
  }, [trials]);

  if (!loading && (!trials || trials.length === 0)) return null;

  const displayCount = Math.min((trials || []).length, MAX_TIMELINE_TRIALS);
  const chartHeight = Math.max(220, displayCount * 58 + 60);

  return (
    <GlassCard sx={{ overflow: "visible" }}>
      <Box p={3} sx={{ position: "relative", zIndex: 1, overflow: "visible" }}>
        {/* Section header */}
        <Box display="flex" alignItems="center" mb={0.5}>
          <IoTimeOutline size={20} color="#60A5FA" />
          <Typography
            variant="h6"
            sx={{
              background: GRADIENTS.chartAccent,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: 700,
              fontSize: "1.2rem",
              ml: 1,
            }}
          >
            Biggest Clinical Threats
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.8rem", mb: 2 }}
        >
          Top competitor trials by enrollment size &mdash; when they are expected to hit the market
        </Typography>

        {/* Legend */}
        <Box display="flex" gap={2.5} mb={2} flexWrap="wrap">
          {[
            { label: "Recruiting", color: STATUS_COLORS.RECRUITING },
            { label: "Active / Not Yet Recruiting", color: STATUS_COLORS.ACTIVE },
            { label: "Completed", color: STATUS_COLORS.COMPLETED },
          ].map((item) => (
            <Box key={item.label} display="flex" alignItems="center" gap={0.7}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "3px",
                  backgroundColor: item.color,
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.72rem" }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Loading skeleton — matches other pages */}
        {loading ? (
          <StandardLoadingIndicator containerHeight={310} />
        ) : (
          /* Chart — overflow:visible so tooltip is never clipped by GlassCard */
          <Box
            sx={{
              "& .apexcharts-canvas": { overflow: "visible !important" },
              "& .apexcharts-svg":    { overflow: "visible !important" },
            }}
          >
            <Chart
              options={chartOptions}
              series={series}
              type="rangeBar"
              height={chartHeight}
            />
          </Box>
        )}
      </Box>
    </GlassCard>
  );
};

export default TimelineRadar;
